import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import type { LeafletEventHandlerFnMap } from "leaflet";
import * as L from "leaflet";
import { twMerge } from "tailwind-merge";

import "leaflet/dist/leaflet.css";

const DEFAULT_ZOOM = 16;
const DEFAULT_CENTER = { lat: 0, lon: 0 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export interface OsmMapLocation {
    id: string;
    latitude: number;
    longitude: number;
    /** Optional tag displayed beside this marker. */
    label?: string;
}

type NormalizedLocation = OsmMapLocation & { lat: number; lon: number };

export interface OsmMapProps {
    locations: OsmMapLocation[];
    tileUrl: string;
    zoom?: number;
    className?: string;
    /** Enables the normal Leaflet pan and zoom gestures. */
    interactive?: boolean;
    /** Fit the initial viewport to all supplied locations. */
    fitToLocations?: boolean;
    /** Keep one location centered while it changes, as the control GPS view does. */
    followLocationId?: string;
    /** Increment to explicitly recenter a followed location. */
    recenterKey?: string | number;
    /** Used by resizable consumers to invalidate Leaflet's measured size. */
    resizeKey?: string | number;
    /** Preserve consumer-specific tile failure behavior. */
    tileEventHandlers?: LeafletEventHandlerFnMap;
    /** Remount the map when a static location changes. */
    mapKey?: string;
}

const markerIcon = new L.DivIcon({
    html: `<div class="min-w-[32px] aspect-square max-w-[32px]"><img src="${
        require("assets/svgs/navigation/map/gps_loc.svg").default
    }" class="w-full h-full object-contain" draggable="false" /></div>`,
    className: "border-0",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
});

const normalizeLocations = (locations: OsmMapLocation[]): NormalizedLocation[] =>
    locations.flatMap((location) => {
        const latitude = Number(location.latitude);
        const longitude = Number(location.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

        return [
            {
                ...location,
                lat: clamp(latitude, -85, 85),
                lon: clamp(longitude, -180, 180),
            },
        ];
    });

const FollowLocation: React.FC<{
    location?: NormalizedLocation;
    enabled: boolean;
    recenterKey?: string | number;
}> = ({ location, enabled, recenterKey }) => {
    const map = useMap();
    const draggingRef = useRef(false);
    const locationRef = useRef(location);
    locationRef.current = location;

    const snapToLocation = useCallback(() => {
        const current = locationRef.current;
        if (!current) return;
        map.setView([current.lat, current.lon], map.getZoom(), { animate: false });
    }, [map]);

    useEffect(() => {
        if (!enabled || !location || draggingRef.current) return;

        map.invalidateSize({ animate: false });
        const frame = requestAnimationFrame(snapToLocation);
        return () => cancelAnimationFrame(frame);
    }, [enabled, location?.lat, location?.lon, map, recenterKey, snapToLocation]);

    useEffect(() => {
        if (!enabled) {
            draggingRef.current = false;
            return;
        }

        const onDragStart = () => {
            draggingRef.current = true;
        };
        const onDragEnd = () => {
            draggingRef.current = false;
            snapToLocation();
        };
        const onZoom = () => {
            if (!draggingRef.current) snapToLocation();
        };

        map.on("dragstart", onDragStart);
        map.on("dragend", onDragEnd);
        map.on("zoom", onZoom);
        map.on("zoomend", onZoom);
        return () => {
            map.off("dragstart", onDragStart);
            map.off("dragend", onDragEnd);
            map.off("zoom", onZoom);
            map.off("zoomend", onZoom);
        };
    }, [enabled, map, snapToLocation]);

    return null;
};

const MapViewportSync: React.FC<{
    locations: NormalizedLocation[];
    fitToLocations: boolean;
    followLocation?: NormalizedLocation;
    resizeKey?: string | number;
    zoom: number;
}> = ({ locations, fitToLocations, followLocation, resizeKey, zoom }) => {
    const map = useMap();
    const locationsKey = locations
        .map(({ id, lat, lon }) => `${id}:${lat.toFixed(6)},${lon.toFixed(6)}`)
        .join("|");

    useEffect(() => {
        const container = map.getContainer();
        const invalidate = () => map.invalidateSize({ animate: false });
        const timeoutId = window.setTimeout(invalidate, 50);
        const resizeObserver =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(invalidate)
                : null;
        resizeObserver?.observe(container);

        return () => {
            window.clearTimeout(timeoutId);
            resizeObserver?.disconnect();
        };
    }, [map]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            map.invalidateSize({ animate: false });

            if (fitToLocations) {
                if (locations.length > 1) {
                    map.fitBounds(
                        locations.map(({ lat, lon }) => [lat, lon] as [number, number]),
                        { padding: [32, 32], maxZoom: zoom }
                    );
                } else if (locations.length === 1) {
                    map.setView([locations[0].lat, locations[0].lon], zoom, { animate: false });
                }
            }

            if (followLocation) {
                map.setView([followLocation.lat, followLocation.lon], map.getZoom(), {
                    animate: false,
                });
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [
        fitToLocations,
        locationsKey,
        locations.length,
        followLocation?.lat,
        followLocation?.lon,
        map,
        resizeKey,
        zoom,
    ]);

    return null;
};

/** Provider-free OSM renderer shared by the control GPS view and dashboard maps. */
const OsmMap: React.FC<OsmMapProps> = ({
    locations,
    tileUrl,
    zoom = DEFAULT_ZOOM,
    className,
    interactive = true,
    fitToLocations = false,
    followLocationId,
    recenterKey,
    resizeKey,
    tileEventHandlers,
    mapKey,
}) => {
    const normalizedLocations = useMemo(() => normalizeLocations(locations), [locations]);
    const firstLocation = normalizedLocations[0];
    const followLocation = normalizedLocations.find(({ id }) => id === followLocationId);
    const center = firstLocation ?? DEFAULT_CENTER;

    return (
        <div className={twMerge("relative h-full w-full", className)}>
            <MapContainer
                key={mapKey}
                center={[center.lat, center.lon]}
                zoom={zoom}
                className="h-full w-full"
                zoomControl={false}
                attributionControl={false}
                dragging={interactive}
                scrollWheelZoom={interactive}
                doubleClickZoom={interactive}
                touchZoom={interactive}
                boxZoom={interactive}
                keyboard={interactive}
            >
                <TileLayer
                    url={tileUrl}
                    referrerPolicy="strict-origin-when-cross-origin"
                    eventHandlers={tileEventHandlers}
                />
                {normalizedLocations.map((location) => (
                    <Marker
                        key={location.id}
                        position={[location.lat, location.lon]}
                        icon={markerIcon}
                    >
                        {location.label ? (
                            <Tooltip direction="top" offset={[0, -16]} permanent>
                                {location.label}
                            </Tooltip>
                        ) : null}
                    </Marker>
                ))}
                <MapViewportSync
                    locations={normalizedLocations}
                    fitToLocations={fitToLocations}
                    followLocation={followLocation}
                    resizeKey={resizeKey}
                    zoom={zoom}
                />
                <FollowLocation
                    location={followLocation}
                    enabled={Boolean(followLocation)}
                    recenterKey={recenterKey}
                />
            </MapContainer>
        </div>
    );
};

export default OsmMap;
